import {
  NavigationEntry,
  RessortEntry,
  Article,
  ResponseEntry,
  RessortModuleEntry,
  CommentEntry,
  JsonResponseEntry,
  TeaserEntry,
  SearchResult,
  CmpMappingEntry,
  CmpMappingResponse,
} from "./interfaces";
import { getNavigationEntry } from "./navigation";
import { getRessortEntry, getRessortModuleEntry } from "./ressort";
import { getArticleEntry, normalizeTeaserTypes } from "./article";
import { getComments } from "./comments";
import { getCmpMappingWithFrameSize } from "./cmpmapping";

import { logBugsnagError } from '../lib/bugsnag'
import { AppTheme } from "./enums";
import { getPageInformation } from "./pageInformation";
import { getImageEntries } from "./images";


const idRegEx = new RegExp(/(\d*\.\d+)/, 'i');

function isIdAllowed(id: string) {
  if(!idRegEx.test(id)) {
    return false;
  }
  return true
}

const backendUrl = process.env.EXTERNAL_BACKEND_URL;
const backendPrefix = process.env.EXTERNAL_BACKEND_PREFIX;

function notifyBugsnag(message: string) {
  logBugsnagError(new Error(message))
}

async function loadJson(url, prefix) : Promise<JsonResponseEntry | null> {
  if(prefix) {
    url = `/${backendPrefix}/${url}`
  }
  try {
    const res = await fetch(backendUrl + url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: process.env.API_KEY || '',
      },
    });
    if (res.status === 200) {
      const json = await res.json();
      return getJsonResponseEntry(res,json)
    }
    else {
      notifyBugsnag(`Error loadJson Response != 200: ${res.status} ${url} ${res}`)
      return getJsonResponseEntry(res, null)
    }
    
  } catch (error) {
    notifyBugsnag(`Error loadJson: ${error.toString()} ${url}`)
  }
  return null
}

export async function loadNavigation(
  id: string | undefined
): Promise<NavigationEntry | null> {
  let url = "navigation";
  if (id) {
    url += "/" + id;
  }
  const jsonResponse = await loadJson(url, true);
  try {
    if (jsonResponse && jsonResponse.status == 200 && jsonResponse.json) {
      return getNavigationEntry(jsonResponse.json);
    }
  } catch (error) {
    notifyBugsnag(`Error loadNavigation: ${error.toString()}`)
  }
  return null;
}

export async function loadRessort(id: string): Promise<RessortEntry | null> {
  let url = "ressort/" + id;

  if(!isIdAllowed(id)) {
    return null
  }

  const jsonResponse = await loadJson(url, true);
  try {
    if (jsonResponse && jsonResponse.status == 200 && jsonResponse.json) {
      return getRessortEntry(jsonResponse.json);
    }
    else {
      return getStatusResponseEntry(jsonResponse?.status || 500) as RessortEntry
    }
  } catch (error) {
    notifyBugsnag(`Error loadRessort: ${error.toString()}`)
  }
  return null
}

export async function loadPersonalRessort(id: string): Promise<RessortEntry | null> {  
  let url = `ressort/${id}?isFavoriteView=true&ignoreAds=true&limit=3`;

  if(!isIdAllowed(id)) {
    return null
  }

  const jsonResponse = await loadJson(url, true);
  try {
    if (jsonResponse && jsonResponse.status == 200 && jsonResponse.json) {
      return getRessortEntry(jsonResponse.json);
    }
  } catch (error) {
    notifyBugsnag(`Error loadRessort: ${error.toString()}`)
  }
  return null
}

export async function loadModule(
  ressortId: string | undefined,
  id: string,
  page: number,
  excludeIdList: string,
  categories?: string, 
  cinemas?: string, 
  cities?: string, 
  dates?: string, 
  kitchens?: string, 
  locations?: string, 
  names?: string, 
  offers?: string, 
  text?: string, 
  types?: string,
  cXenseUserId?: string,
  ssoUserId?: string
): Promise<RessortModuleEntry | null> {
  let url = "module/" + id + "/" + page + "?";
  if(ressortId && ressortId != "" && ressortId != "undefined") {
    url += "&ressortId=" + ressortId;
  }
  if(excludeIdList) {
    url += "&excludeIdList=" + excludeIdList
  }
  if(categories) {
    url += "&categories=" + categories
  }
  if(cinemas) {
    url += "&cinemas=" + cinemas
  }
  if(cities) {
    url += "&cities=" + cities
  }
  if(dates) {
    url += "&dates=" + dates
  }
  if(kitchens) {
    url += "&kitchens=" + kitchens
  }
  if(locations) {
    url += "&locations=" + locations
  }
  if(names) {
    url += "&names=" + names
  }
  if(offers) {
    url += "&offers=" + offers
  }
  if(text) {
    url += "&text=" + text
  }
  if(types) {
    url += "&types=" + types
  }
  if(cXenseUserId) {
    url += "&cXenseUserid=" + cXenseUserId
  }
  if(ssoUserId) {
    url += "&ssoUserId=" + ssoUserId
  }
  url = url.replace("?&", "?")
  const jsonResponse = await loadJson(url, true);
  try {
    if (jsonResponse && jsonResponse.status == 200 && jsonResponse.json) {

      return getRessortModuleEntry(jsonResponse.json, Math.random()) as RessortModuleEntry | null;
    }
  } catch (error) {
    notifyBugsnag(`Error loadModule: ${error.toString()}`)
  }
  return null
}

export async function loadModuleByTeaserIds(
  ids: string
): Promise<RessortModuleEntry | null> {
  let url = `module?ids=${ids}`;
  const jsonResponse = await loadJson(url, true);
  try {
    if (jsonResponse && jsonResponse.status == 200 && jsonResponse.json) {
      return getRessortModuleEntry(jsonResponse.json, Math.random()) as RessortModuleEntry | null;
    }
  } catch (error) {
    notifyBugsnag(`Error loadComments: ${error.toString()}`)
  }
  return null
}

export async function loadCmpMapping(): Promise<CmpMappingResponse | null> {
  let url = '/system/cmp-mapping'
  const jsonResponse = await loadJson(url, false);
  try {
    if (jsonResponse && jsonResponse.status == 200 && jsonResponse.json) {
      const cmpMapping = {
        setCmpMappingWithFrameSize: getCmpMappingWithFrameSize(jsonResponse.json),
        cmpVendorIdMapping: jsonResponse.json.entries.map((entry: CmpMappingEntry) => ({
          identifier: entry.identifier,
          vendorId: entry.vendorId
        })).reduce((acc, curr) => {
          acc[curr.identifier] = curr.vendorId;
          return acc;
        }, {} as Record<string, string>)
      }

      return cmpMapping;
    }
  } catch (error) {
    console.error("** loadCmpMapping", error)
    notifyBugsnag(`Error loadCmpMapping: ${error.toString()}`)
  }
  return null
}

export async function loadArticle(id: string): Promise<Article | null> {
  if(!isIdAllowed(id)) {
    return null
  }

  let url = "detail/" + id;

  const regex = /(\d+\.\d+)/;
  const match = id.match(regex);

  if (match) {
    url = "detail/" + match[0];
  }
  
  const jsonResponse = await loadJson(url, true);
  try {
    if (jsonResponse && jsonResponse.status == 200 && jsonResponse.json) {
      return getArticleEntry(jsonResponse.json);
    }
  } catch (error) {
    notifyBugsnag(`Error loadArticle: ${error.toString()}`)
  }
  return null
}

export async function loadComments(
  id: string,
  page: number,
  pageSize: number
): Promise<CommentEntry[] | null> {
  let url = `comments/${id}/${page}/${pageSize}`;
  const jsonResponse = await loadJson(url, true);
  try {
    if (jsonResponse && jsonResponse.status == 200 && jsonResponse.json) {
      return getComments(jsonResponse.json);
    }
  } catch (error) {
    notifyBugsnag(`Error loadComments: ${error.toString()}`)
  }
  return null
}

export function getResponseEntry(res: Response): ResponseEntry {
  return {
    status: res.status,
  };
}

export function getStatusResponseEntry(status: number): ResponseEntry {
  return {
    status: status,
  };
}

export function getJsonResponseEntry(res: Response, json: any): JsonResponseEntry {
  return {
    status: res.status,
    json: json
  };
}


export function getTimeoutResponseEntry(res: Response): ResponseEntry {
  return {
    status: 504,
  };
}

export async function postVote(
  id: string,
  data: any
): Promise<JsonResponseEntry> {
  try {
    const response = await fetch(`${backendUrl}/${backendPrefix}/voting/${id}`, {
      method: 'POST',
      headers: {
        Authorization: process.env.API_KEY || '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    return {
      status: response.status,
      json: result
    }
  } catch (error) {
    logBugsnagError(error)
    console.error('Error during POST request:', error.message);
    return {
      status: 500,
      json: null
    }
  }
}

export async function postVoteDoiMail(
  id: string,
  isOptin: boolean,
  data: any
): Promise<JsonResponseEntry> {
  try {
    const response = await fetch(`${backendUrl}/${backendPrefix}/voting/send-mails/${id}?isOptin=${isOptin}`, {
      method: 'POST',
      headers: {
        Authorization: process.env.API_KEY || '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    return {
      status: response.status,
      json: result
    }
  } catch (error) {
    logBugsnagError(error)
    console.error('Error during POST request:', error.message);
    return {
      status: 500,
      json: null
    }
  }
}

export async function postVoteData(
  data: string
): Promise<JsonResponseEntry> {
  try {
    const response = await fetch(`${backendUrl}/${backendPrefix}/voting/data/${encodeURIComponent(data)}`, {
      method: 'POST',
      headers: {
        Authorization: process.env.API_KEY || '',
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    return {
      status: response.status,
      json: result
    }
  } catch (error) {
    logBugsnagError(error)
    console.error('Error during POST request:', error.message);
    return {
      status: 500,
      json: null
    }
  }
}

export async function postOptInData(
  optinData: string
): Promise<JsonResponseEntry> {
  try {
    const response = await fetch(`${backendUrl}/${backendPrefix}/voting/optin-data/${encodeURIComponent(optinData)}`, {
      method: 'POST',
      headers: {
        Authorization: process.env.API_KEY || '',
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    return {
      status: response.status,
      json: result
    }
  } catch (error) {
    logBugsnagError(error)
    console.error('Error during POST request:', error.message);
    return {
      status: 500,
      json: null
    }
  }
}

export async function search(
  query: string,
  sort: string,
  page: number
): Promise<SearchResult | null> {
  const pageSize = 10;
  let url = `search/${query}/${sort}/${page}/${pageSize}`;
  const jsonResponse = await loadJson(url, true);
  try {    
    if (jsonResponse && jsonResponse.status == 200) {
      const jsonResult = jsonResponse.json
      let teasers = []
      if(jsonResult?.teasers) {
        teasers = jsonResult?.teasers.map((entry) => { return {...entry, images: getImageEntries(entry), teaserTypes: normalizeTeaserTypes(entry.teaserTypes ?? [])}});
      }
      return {...jsonResult, teasers: teasers, showLoadMore: jsonResult.showLoadMoreButton === true, 
        pageInformation: getPageInformation(jsonResult)} as SearchResult;
    }
  } catch (error) {
    notifyBugsnag(`Error loadModule: ${error.toString()}`)
  }
  return null
}

export async function searchAutocomplete(
  query: string,
): Promise<string[] | null> {
  let url = `search-autocomplete/${query}`;
  const jsonResponse = await loadJson(url, true);
  try {    
    if (jsonResponse && jsonResponse.status == 200) {
      const jsonResult = jsonResponse.json
      return jsonResult
    }
  } catch (error) {
    notifyBugsnag(`Error loadModule: ${error.toString()}`)
    return []
  }
  return null
}

export async function postComment(
  id: string,
  data: { text: string, ssoJwtToken: string }
): Promise<JsonResponseEntry> {
  const url = `${backendUrl}/${backendPrefix}/comment/${id}`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: process.env.API_KEY || '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    return {
      status: response.status,
      json: result
    }
  } catch (error) {
    logBugsnagError(error)
    console.error('Error during POST request:', error.message);
    return {
      status: 500,
      json: null
    }
  }
}